import KoraxPendingClaims from "../KoraxPendingClaims";
import Tooltip from "@material-ui/core/Tooltip";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputBase,
  Popover,
  Typography
} from "@material-ui/core";

import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import InboxIcon from "@material-ui/icons/Inbox";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import ForumIcon from "@material-ui/icons/Forum";
import GroupIcon from "@material-ui/icons/Group";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ViewListIcon from "@material-ui/icons/ViewList";
import TuneIcon from "@material-ui/icons/Tune";
import VisibilityOffOutlinedIcon from "@material-ui/icons/VisibilityOffOutlined";
import CloseIcon from "@material-ui/icons/Close";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import NewTicketModal from "../NewTicketModal";
import { AuthContext } from "../../context/Auth/AuthContext";

import {
  getKoraxModeColors,
  koraxTokens
} from "../../designSystem";

const useStyles = makeStyles(theme => {
  const colors = getKoraxModeColors(theme.mode);
  const dark = theme.mode === "dark";

  return {
    root: {
      height: "100%",
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      backgroundColor: colors.surface
    },

    top: {
      flexShrink: 0,
      padding: "14px 14px 10px",
      backgroundColor: colors.surface,
      borderBottom: `1px solid ${colors.border}`
    },

    header: {
      minHeight: 42,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      marginBottom: 10
    },

    heading: {
      minWidth: 0
    },

    titleRow: {
      display: "flex",
      alignItems: "center",
      gap: 8
    },

    title: {
      margin: 0,
      color: colors.textPrimary,
      fontSize: 21,
      fontWeight: 750,
      lineHeight: 1.15,
      letterSpacing: "-0.025em"
    },

    activeCount: {
      minWidth: 24,
      height: 22,
      padding: "0 7px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: koraxTokens.radius.pill,
      backgroundColor: dark
        ? "rgba(56,189,248,.12)"
        : koraxTokens.colors.primarySoft,
      color: theme.palette.primary.main,
      fontSize: 10.5,
      fontWeight: 750
    },

    subtitle: {
      marginTop: 3,
      color: colors.textMuted,
      fontSize: 11.5,
      lineHeight: 1.3
    },

    addButton: {
      width: 38,
      height: 38,
      padding: 0,
      borderRadius: 11,
      color: "#FFFFFF",
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${koraxTokens.colors.primaryHover})`,
      boxShadow: "0 8px 18px rgba(21,94,239,.20)",

      "&:hover": {
        background: koraxTokens.colors.primaryHover,
        transform: "translateY(-1px)"
      },

      "& .MuiSvgIcon-root": {
        fontSize: 20
      }
    },

    search: {
      height: 40,
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "0 11px",
      borderRadius: 11,
      border: `1px solid ${colors.border}`,
      backgroundColor: dark ? "#182230" : "#F8FAFC",
      transition: koraxTokens.transitions.fast,

      "&:focus-within": {
        borderColor: theme.palette.primary.main,
        backgroundColor: colors.surface,
        boxShadow: koraxTokens.shadows.focus
      }
    },

    searchIcon: {
      flexShrink: 0,
      color: colors.textMuted,
      fontSize: 18
    },

    searchInput: {
      flex: 1,
      minWidth: 0,
      color: colors.textPrimary,
      fontSize: 13,

      "& input::placeholder": {
        color: colors.textMuted,
        opacity: 1
      }
    },

    searchRow: {
      display: "flex",
      alignItems: "center",
      gap: 7
    },

    filterButton: {
      width: 40,
      height: 40,
      flexShrink: 0,
      padding: 0,
      borderRadius: 11,
      border: `1px solid ${colors.border}`,
      backgroundColor: dark ? "#182230" : "#F8FAFC",
      color: colors.textSecondary
    },

    filterPaper: {
      width: 330,
      maxWidth: "calc(100vw - 24px)",
      maxHeight: "calc(100vh - 40px)",
      marginTop: 7,
      borderRadius: 14,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      overflow: "hidden"
    },

    filterHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 12px",
      borderBottom: `1px solid ${colors.border}`
    },

    filterBody: {
      maxHeight: 440,
      overflowY: "auto",
      padding: "4px 12px 10px"
    },

    filterFooter: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      padding: "10px 12px",
      borderTop: `1px solid ${colors.border}`
    },

    activeFilters: {
      display: "flex",
      alignItems: "center",
      gap: 5,
      flexWrap: "wrap",
      marginTop: 7
    },

    activeFilterChip: {
      height: 25,
      borderRadius: 8,
      backgroundColor: dark
        ? "rgba(56,189,248,.10)"
        : koraxTokens.colors.primarySoft,
      color: theme.palette.primary.main,
      fontSize: 10.5,
      fontWeight: 700
    },

    tabs: {
      width: "100%",
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
      gap: 5,
      marginTop: 9,
      overflowX: "auto",
      overflowY: "hidden",
      paddingBottom: 5,
      WebkitOverflowScrolling: "touch",
      scrollbarWidth: "thin",

      "&::-webkit-scrollbar": {
        height: 4
      },

      "&::-webkit-scrollbar-track": {
        background: "transparent"
      },

      "&::-webkit-scrollbar-thumb": {
        borderRadius: 999,
        backgroundColor: dark
          ? "rgba(148,163,184,.35)"
          : "rgba(100,116,139,.25)"
      }
    },

    scopeBar: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      width: "fit-content",
      maxWidth: "100%",
      marginTop: 7,
      padding: 3,
      borderRadius: 9,
      backgroundColor: dark ? "#182230" : "#F1F5F9"
    },

    scopeButton: {
      minHeight: 27,
      padding: "0 10px",
      border: "none",
      borderRadius: 7,
      background: "transparent",
      color: colors.textMuted,
      fontFamily: koraxTokens.typography.fontFamily,
      fontSize: 10.5,
      fontWeight: 700,
      cursor: "pointer",
      transition: koraxTokens.transitions.fast
    },

    scopeButtonActive: {
      backgroundColor: colors.surface,
      color: theme.palette.primary.main,
      boxShadow: dark
        ? "0 1px 4px rgba(0,0,0,.20)"
        : "0 1px 4px rgba(15,23,42,.10)"
    },

    tab: {
      flex: "0 0 auto",
      minHeight: 30,
      padding: "0 9px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 5,
      whiteSpace: "nowrap",

      borderRadius: 9,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.textSecondary,

      fontFamily: koraxTokens.typography.fontFamily,
      fontSize: 10.5,
      fontWeight: 650,
      cursor: "pointer",
      transition: koraxTokens.transitions.fast,

      "&:hover": {
        borderColor: dark
          ? "rgba(56,189,248,.26)"
          : "rgba(21,94,239,.20)",
        color: theme.palette.primary.main,
        backgroundColor: dark
          ? "rgba(56,189,248,.06)"
          : "#F8FAFF"
      }
    },

    tabActive: {
      color: theme.palette.primary.main,
      borderColor: dark
        ? "rgba(56,189,248,.32)"
        : "rgba(21,94,239,.25)",
      backgroundColor: dark
        ? "rgba(56,189,248,.10)"
        : koraxTokens.colors.primarySoft
    },

    tabIcon: {
      fontSize: 14
    },

    tabCount: {
      minWidth: 17,
      height: 17,
      padding: "0 4px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: koraxTokens.radius.pill,
      backgroundColor: dark
        ? "rgba(148,163,184,.14)"
        : "#EEF2F6",
      color: colors.textSecondary,
      fontSize: 9,
      fontWeight: 750
    },

    tabCountActive: {
      color: theme.palette.primary.main,
      backgroundColor: dark
        ? "rgba(56,189,248,.14)"
        : "#FFFFFF"
    },

    list: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      padding: "8px 8px 14px",
      backgroundColor: dark ? "#0F172A" : "#F8FAFC",
      ...theme.scrollbarStyles
    },

    card: {
      minHeight: 74,
      display: "grid",
      gridTemplateColumns: "40px minmax(0, 1fr) auto",
      alignItems: "center",
      gap: 9,

      marginBottom: 5,
      padding: "9px 9px",

      borderRadius: 12,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      cursor: "pointer",
      transition: koraxTokens.transitions.fast,

      "&:hover": {
        borderColor: dark
          ? "rgba(56,189,248,.25)"
          : "rgba(21,94,239,.18)",
        backgroundColor: dark ? "#142033" : "#FFFFFF",
        boxShadow: koraxTokens.shadows.sm,
        transform: "translateY(-1px)"
      }
    },

    cardActive: {
      borderColor: dark
        ? "rgba(56,189,248,.38)"
        : "rgba(21,94,239,.34)",
      backgroundColor: dark
        ? "rgba(30,58,138,.22)"
        : koraxTokens.colors.primarySoft,
      boxShadow: "0 0 0 1px rgba(21,94,239,.05)"
    },

    avatar: {
      width: 40,
      height: 40,
      backgroundColor: theme.palette.primary.main,
      color: "#FFFFFF",
      fontSize: 12,
      fontWeight: 750
    },

    content: {
      minWidth: 0
    },

    name: {
      margin: 0,
      color: colors.textPrimary,
      fontWeight: 700,
      fontSize: 13,
      lineHeight: 1.25,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis"
    },

    message: {
      marginTop: 3,
      color: colors.textMuted,
      fontSize: 11.5,
      lineHeight: 1.35,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis"
    },

    meta: {
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 4,
      rowGap: 5,
      marginTop: 6,
      overflow: "visible",

      "& .MuiChip-root": {
        flexShrink: 0,
        maxWidth: "100%",
        height: 20,
        borderRadius: koraxTokens.radius.pill,
        fontSize: 9,
        fontWeight: 700
      },

      "& .MuiChip-label": {
        paddingLeft: 7,
        paddingRight: 7,
        overflow: "visible",
        textOverflow: "clip",
        whiteSpace: "nowrap"
      }
    },

    right: {
      alignSelf: "stretch",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      justifyContent: "space-between",
      padding: "2px 0"
    },

    rightTop: {
      display: "flex",
      alignItems: "center",
      gap: 3
    },

    pinButton: {
      width: 23,
      height: 23,
      padding: 3,
      border: 0,
      borderRadius: 6,
      background: "transparent",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      color: colors.textMuted,
      opacity: 0.55,
      transition: koraxTokens.transitions.fast,

      "&:hover": {
        opacity: 1,
        color: theme.palette.primary.main,
        backgroundColor: dark
          ? "rgba(56,189,248,.10)"
          : koraxTokens.colors.primarySoft
      }
    },

    pinButtonActive: {
      opacity: 1,
      color: theme.palette.primary.main
    },

    time: {
      color: colors.textMuted,
      fontSize: 9.5,
      fontWeight: 600,
      whiteSpace: "nowrap"
    },

    unread: {
      minWidth: 18,
      height: 18,
      padding: "0 5px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: koraxTokens.radius.pill,
      backgroundColor: koraxTokens.colors.success,
      color: "#FFFFFF",
      fontSize: 9,
      fontWeight: 800
    },

    empty: {
      minHeight: 180,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
      textAlign: "center",
      color: colors.textMuted
    },

    emptyIcon: {
      width: 42,
      height: 42,
      marginBottom: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 13,
      backgroundColor: dark
        ? "rgba(56,189,248,.10)"
        : koraxTokens.colors.primarySoft,
      color: theme.palette.primary.main
    },

    emptyTitle: {
      color: colors.textPrimary,
      fontSize: 13,
      fontWeight: 700
    },

    emptyText: {
      maxWidth: 220,
      marginTop: 4,
      fontSize: 11.5,
      lineHeight: 1.4
    },

    loading: {
      display: "flex",
      justifyContent: "center",
      padding: 24
    }
  };
});

const tabs = [
  { key: "all", label: "Todas", icon: ViewListIcon },
  { key: "open", label: "Atendendo", icon: ForumIcon },
  { key: "pending", label: "Recepção", icon: InboxIcon },
  { key: "group", label: "Grupos", icon: GroupIcon },
  { key: "closed", label: "Finalizados", icon: CheckCircleIcon }
];

const createEmptyFilters = () => ({
  queueIds: [],
  userIds: [],
  tagIds: [],
  whatsappIds: [],
  withoutAi: false
});

const KoraxConversationsManager = () => {
  const classes = useStyles();
  const history = useHistory();
  const { ticketId } = useParams();
  const { user, socket } = useContext(AuthContext);
  const companyId = user?.companyId;

  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [openScope, setOpenScope] = useState("mine");

  const allUserChat = String(user?.allUserChat || "").toLowerCase();
  const allHistoric = String(user?.allHistoric || "").toLowerCase();
  const isAdmin = String(user?.profile || "").toLowerCase() === "admin";

  const userQueues = Array.isArray(user?.queues)
    ? user.queues
    : [];

  const canSeeSector =
    isAdmin || userQueues.length > 0;

  const sectorLabel =
    isAdmin
      ? "Setores"
      : userQueues.length === 1
      ? userQueues[0]?.name || "Setor"
      : "Setores";

  const canSeeTeam =
    isAdmin ||
    allUserChat === "enabled" ||
    allUserChat === "enable";

  const canSeeAll =
    isAdmin ||
    (
      canSeeTeam &&
      (
        allHistoric === "enabled" ||
        allHistoric === "enable"
      )
    );

  useEffect(() => {
    const nextScope = canSeeAll
      ? "all"
      : canSeeTeam
      ? "team"
      : "mine";

    setOpenScope(current =>
      current === nextScope ? current : nextScope
    );
  }, [canSeeAll, canSeeTeam]);

  const openShowAll =
    openScope === "mine"
      ? "false"
      : "true";

  const [active, setActive] = useState("mine");
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [searchParam, setSearchParam] = useState("");
  const [loading, setLoading] = useState(false);

  // Paginacao real da Central de Conversas
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // KORAX FILTERS V2
  const [listCount, setListCount] = useState(0);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const [filterOptions, setFilterOptions] = useState({
    queues: [],
    users: [],
    tags: [],
    whatsapps: []
  });

  // ============================================================
  // KORAX CONVERSATIONS V3
  //
  // Meus -> tickets sob minha responsabilidade
  // WhatsApps -> uma aba automatica por conexao
  // Todos -> todas as conversas permitidas ao usuario
  // ============================================================

  const [onlyUnread, setOnlyUnread] = useState(false);
  // KORAX - conexoes permitidas ao usuario.
  // Lista vazia/null mantem o comportamento legado.
  const allowedWhatsappIds = useMemo(
    () =>
      Array.isArray(user?.whatsappIds)
        ? user.whatsappIds
            .map(Number)
            .filter(
              id =>
                Number.isInteger(id) &&
                id > 0
            )
        : [],
    [user?.whatsappIds]
  );

  const hasWhatsappRestriction =
    !isAdmin &&
    Array.isArray(user?.whatsappIds);


  const conversationTabs = useMemo(() => {
    const whatsappTabs = filterOptions.whatsapps
      .filter(
        item =>
          (
            !item.channel ||
            item.channel === "whatsapp"
          ) &&
          (
            !hasWhatsappRestriction ||
            allowedWhatsappIds.includes(
              Number(item.id)
            )
          )
      )
      .map(item => ({
        key: `whatsapp-${item.id}`,
        label: item.name || `WhatsApp ${item.id}`,
        icon: ForumIcon,
        type: "whatsapp",
        whatsappId: Number(item.id)
      }));

    return [
      {
        key: "mine",
        label: "Meus",
        icon: ForumIcon,
        type: "mine"
      },
      ...whatsappTabs,
      {
        key: "all",
        label: "Todos",
        icon: ForumIcon,
        type: "all"
      },
      {
        key: "closed",
        label: "Finalizadas",
        icon: CheckCircleIcon,
        type: "closed"
      }
    ];
  }, [filterOptions.whatsapps,
    hasWhatsappRestriction,
    allowedWhatsappIds]);

  const activeTab = useMemo(
    () =>
      conversationTabs.find(
        tab => tab.key === active
      ) || conversationTabs[0],
    [conversationTabs, active]
  );

  useEffect(() => {
    if (
      conversationTabs.length > 0 &&
      !conversationTabs.some(
        tab => tab.key === active
      )
    ) {
      setActive("mine");
    }
  }, [conversationTabs, active]);

  useEffect(() => {
    if (
      activeTab?.type === "closed" &&
      onlyUnread
    ) {
      setOnlyUnread(false);
    }
  }, [activeTab?.type, onlyUnread]);
const [draftFilters, setDraftFilters] = useState(createEmptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(createEmptyFilters);

  const filterOpen = Boolean(filterAnchorEl);

  const visibleQueues = useMemo(
    () => isAdmin ? filterOptions.queues : userQueues,
    [isAdmin, filterOptions.queues, userQueues]
  );

  const visibleUsers = useMemo(
    () =>
      canSeeTeam
        ? filterOptions.users
        : filterOptions.users.filter(
            item => Number(item.id) === Number(user?.id)
          ),
    [canSeeTeam, filterOptions.users, user?.id]
  );

  useEffect(() => {
    let mounted = true;

    const loadFilterOptions = async () => {
      try {
        const [
          queuesResponse,
          usersResponse,
          tagsResponse,
          whatsappsResponse
        ] = await Promise.all([
          api.get("/queue"),
          api.get("/users/list"),
          api.get("/tags/list"),
          api.get("/whatsapp")
        ]);

        if (!mounted) return;

        setFilterOptions({
          queues: Array.isArray(queuesResponse.data)
            ? queuesResponse.data
            : [],

          users: Array.isArray(usersResponse.data)
            ? usersResponse.data.map(item => ({
                id: item.id,
                name: item.name
              }))
            : [],

          tags: Array.isArray(tagsResponse.data)
            ? tagsResponse.data
            : [],

          whatsapps: Array.isArray(whatsappsResponse.data)
            ? whatsappsResponse.data.map(item => ({
                id: item.id,
                name: item.name,
                channel: item.channel
              }))
            : []
        });
      } catch (err) {
        console.error(
          "Erro ao carregar filtros de conversas",
          err
        );
      }
    };

    loadFilterOptions();

    return () => {
      mounted = false;
    };
  }, [companyId]);

  const cloneFilters = filters => ({
    queueIds: [...filters.queueIds],
    userIds: [...filters.userIds],
    tagIds: [...filters.tagIds],
    whatsappIds: [...filters.whatsappIds],
    withoutAi: Boolean(filters.withoutAi)
  });

  const toggleDraftFilter = (key, id) => {
    setDraftFilters(prev => {
      const current = prev[key] || [];

      const exists = current.some(
        item => Number(item) === Number(id)
      );

      return {
        ...prev,
        [key]: exists
          ? current.filter(
              item => Number(item) !== Number(id)
            )
          : [...current, id]
      };
    });
  };

  const handleOpenFilters = event => {
    setDraftFilters(cloneFilters(appliedFilters));
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilters = () => {
    setFilterAnchorEl(null);
  };

  const applyFilters = () => {
    setAppliedFilters(
      cloneFilters({
        ...draftFilters,
        queueIds: []
      })
    );
    setFilterAnchorEl(null);
  };

  const clearFilters = () => {
    setDraftFilters(createEmptyFilters());
    setAppliedFilters(createEmptyFilters());
    setFilterAnchorEl(null);
  };

  const removeAppliedFilter = (key, id) => {
    if (key === "withoutAi") {
      setAppliedFilters(prev => ({
        ...prev,
        withoutAi: false
      }));

      setDraftFilters(prev => ({
        ...prev,
        withoutAi: false
      }));

      return;
    }

    setAppliedFilters(prev => ({
      ...prev,
      [key]: prev[key].filter(
        item => Number(item) !== Number(id)
      )
    }));

    setDraftFilters(prev => ({
      ...prev,
      [key]: prev[key].filter(
        item => Number(item) !== Number(id)
      )
    }));
  };

  const activeFiltersCount =
    appliedFilters.userIds.length +
    appliedFilters.tagIds.length +
    appliedFilters.whatsappIds.length +
    (
      activeTab?.type !== "closed" &&
      appliedFilters.withoutAi
        ? 1
        : 0
    );

  const filterChips = useMemo(() => {
    const chips = [];

    const pushItems = (key, ids, options, formatter) => {
      ids.forEach(id => {
        const option = options.find(
          item => Number(item.id) === Number(id)
        );

        if (!option) return;

        chips.push({
          key,
          id,
          label: formatter(option)
        });
      });
    };

    pushItems(
      "userIds",
      appliedFilters.userIds,
      visibleUsers,
      option =>
        Number(option.id) === Number(user?.id)
          ? `Eu · ${option.name}`
          : option.name
    );

    pushItems(
      "tagIds",
      appliedFilters.tagIds,
      filterOptions.tags,
      option => option.name
    );

    pushItems(
      "whatsappIds",
      appliedFilters.whatsappIds,
      filterOptions.whatsapps,
      option => option.name
    );

    if (
      activeTab?.type !== "closed" &&
      appliedFilters.withoutAi
    ) {
      chips.push({
        key: "withoutAi",
        id: "withoutAi",
        label: "Aguardando atendimento"
      });
    }

    return chips;
  }, [
    appliedFilters,
    visibleQueues,
    visibleUsers,
    filterOptions.tags,
    filterOptions.whatsapps,
    user?.id,
    activeTab?.type
  ]);

  const renderFilterSection = (
    title,
    key,
    options,
    formatter = option => option.name
  ) => (
    <div>
      <Typography
        style={{
          fontSize: 10,
          fontWeight: 800,
          textTransform: "uppercase",
          opacity: 0.62,
          marginBottom: 4,
          marginTop: 8
        }}
      >
        {title}
      </Typography>

      {options.length > 0 ? (
        options.map(option => (
          <FormControlLabel
            key={`${key}-${option.id}`}
            style={{
              display: "block",
              margin: 0,
              minHeight: 30
            }}
            control={
              <Checkbox
                size="small"
                color="primary"
                checked={draftFilters[key].some(
                  item => Number(item) === Number(option.id)
                )}
                onChange={() =>
                  toggleDraftFilter(key, option.id)
                }
              />
            }
            label={
              <span style={{ fontSize: 12 }}>
                {formatter(option)}
              </span>
            }
          />
        ))
      ) : (
        <Typography
          style={{
            fontSize: 11,
            opacity: 0.55,
            padding: "4px 0 8px"
          }}
        >
          Nenhuma opção disponível
        </Typography>
      )}
    </div>
  );

  const formatTime = value => {
    if (!value) return "";

    const date = new Date(value);
    const now = new Date();
    const diff = Math.floor((now - date) / 60000);

    if (diff < 1) return "agora";
    if (diff < 60) return `${diff} min`;

    if (diff < 1440) {
      return date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit"
      });
    }

    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit"
    });
  };

  // KORAX - impede respostas de filtros/abas antigos.
  const ticketListGenerationRef = React.useRef(0);
  const ticketListQueryRef = React.useRef(null);
  const ticketListRequestRef = React.useRef(0);
  const ticketListAppendRequestRef = React.useRef(0);

  // Invalida respostas da consulta anterior quando
  // aba, filtros ou pesquisa mudam.
  React.useEffect(() => {
    ticketListGenerationRef.current += 1;
    ticketListQueryRef.current = null;
    ticketListRequestRef.current += 1;
  }, [
    active,
    searchParam,
    canSeeAll,
    isAdmin,
    user?.id,
    user?.allowGroup,
    appliedFilters,
    conversationTabs,
    onlyUnread
  ]);

  const loadTickets = useCallback(
    async (
      silent = false,
      requestedPage = 1,
      append = false
    ) => {
      if (append) {
        setLoadingMore(true);
      } else if (!silent) {
        setLoading(true);
      }

    try {
      const selectedTab =
        conversationTabs.find(
          tab => tab.key === active
        ) || conversationTabs[0];

      const isMineView =
        selectedTab?.type === "mine";

      const isWhatsappView =
        selectedTab?.type === "whatsapp";

      const isAllView =
        selectedTab?.type === "all";

      const isClosedView =
        selectedTab?.type === "closed";

      const params = {
        // "search" aqui representa a visualizacao unificada.
        // Nao significa necessariamente que existe texto pesquisado.
        status:
          isClosedView
            ? "closed"
            : "search",
        pageNumber: requestedPage,

        // Admin/usuarios com permissao ampla continuam vendo
        // todas as conversas nas abas de conexao/Todos.
        // "Meus" sempre fica preso ao usuario logado.
        showAll:
          isMineView
            ? "false"
            : canSeeAll
            ? "true"
            : "false",

        // "Meus" significa responsabilidade atual,
        // independente da conexão WhatsApp.
        scope:
          isMineView
            ? "mine"
            : undefined,

        searchParam,

        searchOnMessages:
          searchParam.trim().length >= 3
            ? "true"
            : "false",

        withUnreadMessages:
          !isClosedView && onlyUnread
            ? "true"
            : "false",

        withoutAi:
          !isClosedView && appliedFilters.withoutAi
            ? "true"
            : "false",

        // Operacao ativa nunca mistura tickets finalizados.
        // A aba Finalizadas usa status="closed" dedicado.
        statusFilter:
          isClosedView
            ? undefined
            : JSON.stringify(
                isAdmin || user?.allowGroup === true
                  ? ["open", "pending", "group"]
                  : ["open", "pending"]
              )
      };

      // MEUS = responsabilidade do usuario,
      // independentemente do WhatsApp.
      if (isMineView) {
        params.users = JSON.stringify([
          Number(user.id)
        ]);
      }

      // Uma aba por conexao WhatsApp.
      if (
        isWhatsappView &&
        selectedTab?.whatsappId
      ) {
        params.whatsapps = JSON.stringify([
          Number(selectedTab.whatsappId)
        ]);
      }

      if (isAllView) {
        params.statusFilter = JSON.stringify(
          isAdmin || user?.allowGroup === true
            ? ["open", "pending", "group"]
            : ["open", "pending"]
        );
      }

      if (appliedFilters.queueIds.length > 0) {
        params.queuesFilter = JSON.stringify(
          appliedFilters.queueIds
        );
      }

      if (
        !isMineView &&
        appliedFilters.userIds.length > 0
      ) {
        params.users = JSON.stringify(
          appliedFilters.userIds
        );
      }

      if (appliedFilters.tagIds.length > 0) {
        params.tags = JSON.stringify(
          appliedFilters.tagIds
        );
      }

      if (
        !isWhatsappView &&
        appliedFilters.whatsappIds.length > 0
      ) {
        params.whatsapps = JSON.stringify(
          appliedFilters.whatsappIds
        );
      }

      const queryKey = JSON.stringify({
        ...params,
        pageNumber: undefined
      });

      if (ticketListQueryRef.current !== queryKey) {
        ticketListQueryRef.current = queryKey;
        ticketListGenerationRef.current += 1;
      }

      const generation = ticketListGenerationRef.current;

      // Somente consultas da primeira pagina substituem
      // a versao mais recente da lista.
      const requestId = append
        ? ++ticketListAppendRequestRef.current
        : ++ticketListRequestRef.current;

      const { data } = await api.get("/tickets", {
        params
      });

      if (
        generation !== ticketListGenerationRef.current ||
        queryKey !== ticketListQueryRef.current ||
        (append
          ? requestId !== ticketListAppendRequestRef.current
          : requestId !== ticketListRequestRef.current)
      ) {
        return;
      }

      const nextTickets =
        data.tickets || [];

      if (append) {
        // Scroll infinito:
        // adiciona nova pagina sem duplicar tickets.
        setTickets(previous => {
          const merged = [
            ...previous,
            ...nextTickets
          ];

          const unique = new Map();

          merged.forEach(ticket => {
            unique.set(
              Number(ticket.id),
              ticket
            );
          });

          return Array.from(
            unique.values()
          );
        });

        setPageNumber(requestedPage);
      } else if (
        silent &&
        requestedPage === 1
      ) {
        // Atualizacao silenciosa:
        // descarta tickets antigos que nao pertencem
        // mais ao resultado atual.
        setTickets(nextTickets);
        setPageNumber(1);
      } else {
        // Troca de aba, filtro ou pesquisa:
        // comeca novamente pela pagina 1.
        setTickets(nextTickets);

        if (requestedPage === 1) {
          setPageNumber(1);
        }
      }

      setHasMore(
        Boolean(data.hasMore)
      );

      setListCount(
        typeof data.count === "number"
          ? data.count
          : nextTickets.length
      );
    } catch (err) {
      if (!silent) {
        toastError(err);
      } else {
        console.error(
          "Erro ao atualizar conversas em tempo real",
          err
        );
      }
    } finally {
      if (append) {
        setLoadingMore(false);
      }

      if (!silent) {
        setLoading(false);
      }
    }
  }, [
    active,
    searchParam,
    canSeeAll,
    isAdmin,
    user?.id,
    user?.allowGroup,
    appliedFilters,
    conversationTabs,
    onlyUnread
  ]);

  const loadCounts = useCallback(async () => {
    try {
      const results = await Promise.all(
        conversationTabs.map(tab => {
          const isMineView =
            tab.type === "mine";

          const isWhatsappView =
            tab.type === "whatsapp";

          const isClosedView =
            tab.type === "closed";

          const params = {
            status:
              isClosedView
                ? "closed"
                : "search",
            pageNumber: 1,
            showAll:
              isMineView
                ? "false"
                : canSeeAll
                ? "true"
                : "false",
            scope:
              isMineView
                ? "mine"
                : undefined,
            statusFilter:
              isClosedView
                ? undefined
                : JSON.stringify(
                    isAdmin || user?.allowGroup === true
                      ? ["open", "pending", "group"]
                      : ["open", "pending"]
                  )
          };

          if (isMineView) {
            params.users = JSON.stringify([
              Number(user.id)
            ]);
          }

          if (
            isWhatsappView &&
            tab.whatsappId
          ) {
            params.whatsapps =
              JSON.stringify([
                Number(tab.whatsappId)
              ]);
          }

          return api.get("/tickets", {
            params: {
              ...params,
              countOnly: "true"
            }
          });
        })
      );

      const next = {};

      conversationTabs.forEach(
        (tab, index) => {
          next[tab.key] =
            results[index]?.data?.count ||
            results[index]?.data?.tickets?.length ||
            0;
        }
      );

      setCounts(next);
    } catch (err) {
      console.error(
        "Erro ao carregar contadores de conversas",
        err
      );
    }
  }, [
    conversationTabs,
    canSeeAll,
    isAdmin,
    user?.id,
    user?.allowGroup
  ]);



  useEffect(() => {
    if (openScope === "all" && !canSeeAll) {
      setOpenScope(
        canSeeSector ? "sector" : "mine"
      );
      return;
    }

    if (
      openScope === "sector" &&
      !canSeeSector
    ) {
      setOpenScope("mine");
    }
  }, [
    openScope,
    canSeeAll,
    canSeeSector
  ]);

  useEffect(() => {
    const timeout = setTimeout(loadTickets, 350);

    return () => clearTimeout(timeout);
  }, [loadTickets]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  // KORAX 17AQ - referencias estaveis para eventos socket.
  const socketLoadTicketsRef = React.useRef(loadTickets);
  const socketLoadCountsRef = React.useRef(loadCounts);
  const socketListLastRunRef = React.useRef(0);
  const socketCountsLastRunRef = React.useRef(0);

  socketLoadTicketsRef.current = loadTickets;
  socketLoadCountsRef.current = loadCounts;

  useEffect(() => {
    if (!socket || !companyId) {
      return undefined;
    }

    let listTimer = null;
    let countsTimer = null;

    const refreshList = () => {
      // Agrupa eventos sem adiar indefinidamente a atualizacao.
      if (listTimer) return;

      const elapsed =
        Date.now() - socketListLastRunRef.current;

      const delay = Math.max(350, 5000 - elapsed);

      listTimer = setTimeout(() => {
        listTimer = null;
        socketListLastRunRef.current = Date.now();
        socketLoadTicketsRef.current(true);
      }, delay);
    };

    const refreshAll = () => {
      refreshList();

      if (countsTimer) return;

      const elapsed =
        Date.now() - socketCountsLastRunRef.current;

      const delay = Math.max(700, 20000 - elapsed);

      countsTimer = setTimeout(() => {
        countsTimer = null;
        socketCountsLastRunRef.current = Date.now();
        socketLoadCountsRef.current();
      }, delay);
    };

    const handleTicketEvent = data => {
      const changedTicket = data?.ticket;
      const changedTicketId =
        changedTicket?.id || data?.ticketId;

      // =====================================================
      // KORAX - MEUS EM TEMPO REAL
      //
      // Se uma conversa que estava em "Meus" for transferida
      // para outro atendente, ela precisa desaparecer
      // imediatamente da lista do responsável anterior.
      // =====================================================
      if (
        active === "mine" &&
        changedTicketId
      ) {
        const transferredToAnotherUser =
          changedTicket?.userId !== undefined &&
          changedTicket?.userId !== null &&
          Number(changedTicket.userId) !==
            Number(user?.id);

        // "delete" tambem e emitido pelo backend em mudancas
        // de status e setor. Nao significa exclusao do ticket.
        // Remover imediatamente apenas quando houver
        // destinatario diferente confirmado no evento.
        if (transferredToAnotherUser) {
          setTickets(previous =>
            previous.filter(
              ticket =>
                Number(ticket.id) !==
                Number(changedTicketId)
            )
          );
        }
      }

      refreshAll();
    };

    const handleMessageEvent = () => {
      refreshAll();
    };

    const handleContactEvent = () => {
      refreshList();
    };

    const validSocketStatus =
      ["open", "pending", "closed"].includes(active);

    const joinCurrentStatus = () => {
      if (validSocketStatus) {
        socket.emit("joinTickets", active);
      }
    };

    joinCurrentStatus();

    socket.on("connect", joinCurrentStatus);
    socket.on(`company-${companyId}-ticket`, handleTicketEvent);
    socket.on(`company-${companyId}-appMessage`, handleMessageEvent);
    socket.on(`company-${companyId}-contact`, handleContactEvent);

    return () => {
      if (listTimer) {
        clearTimeout(listTimer);
      }

      if (countsTimer) {
        clearTimeout(countsTimer);
      }

      if (validSocketStatus) {
        socket.emit("joinTicketsLeave", active);
      }

      socket.off("connect", joinCurrentStatus);
      socket.off(`company-${companyId}-ticket`, handleTicketEvent);
      socket.off(`company-${companyId}-appMessage`, handleMessageEvent);
      socket.off(`company-${companyId}-contact`, handleContactEvent);
    };
  }, [
    socket,
    companyId,
    active,
    user?.id
  ]);

  const selectedUuid = ticketId;

  const normalizeSearchText = value =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const getTicketSearchMatch = ticket => {
    const term = searchParam.trim();

    if (
      term.length < 3 ||
      !ticket?.searchMatch?.messageId ||
      !ticket?.searchMatch?.body
    ) {
      return null;
    }

    return ticket.searchMatch;
  };

  const buildSearchSnippet = (text, term) => {
    const original = String(text || "");
    const normalized =
      normalizeSearchText(original);
    const normalizedTerm =
      normalizeSearchText(term);

    const index =
      normalized.indexOf(normalizedTerm);

    if (index < 0) {
      return original.length > 120
        ? `${original.slice(0, 120)}...`
        : original;
    }

    const start = Math.max(
      0,
      index - 48
    );

    const end = Math.min(
      original.length,
      index + term.length + 68
    );

    return `${
      start > 0 ? "..." : ""
    }${original.slice(start, end)}${
      end < original.length ? "..." : ""
    }`;
  };

  const renderHighlightedSearchText = (
    text,
    term
  ) => {
    const original = String(text || "");
    const normalized =
      normalizeSearchText(original);
    const normalizedTerm =
      normalizeSearchText(term);

    const index =
      normalized.indexOf(normalizedTerm);

    if (index < 0 || !normalizedTerm) {
      return original;
    }

    return (
      <>
        {original.slice(0, index)}
        <mark
          style={{
            background: "#FEF08A",
            color: "#713F12",
            borderRadius: 4,
            padding: "0 2px",
            fontWeight: 800
          }}
        >
          {original.slice(
            index,
            index + term.length
          )}
        </mark>
        {original.slice(
          index + term.length
        )}
      </>
    );
  };

  const openTicket = ticket => {
    const match =
      getTicketSearchMatch(ticket);

    const term =
      searchParam.trim();

    if (
      match?.messageId &&
      term.length >= 3
    ) {
      history.push(
        `/tickets/${ticket.uuid}` +
        `?searchMessageId=${match.messageId}` +
        `&searchTicketId=${ticket.id}` +
        `&search=${encodeURIComponent(term)}`
      );

      return;
    }

    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleListScroll = event => {
    const element =
      event.currentTarget;

    if (
      loading ||
      loadingMore ||
      !hasMore
    ) {
      return;
    }

    const remaining =
      element.scrollHeight -
      element.scrollTop -
      element.clientHeight;

    // Comeca a carregar um pouco antes
    // de chegar exatamente ao fim.
    if (remaining > 180) {
      return;
    }

    // Trava adicional para impedir
    // requisicoes duplicadas no mesmo scroll.
    if (
      element.dataset.loadingMore ===
      "true"
    ) {
      return;
    }

    element.dataset.loadingMore =
      "true";

    const nextPage =
      pageNumber + 1;

    Promise.resolve(
      loadTickets(
        true,
        nextPage,
        true
      )
    ).finally(() => {
      element.dataset.loadingMore =
        "false";
    });
  };

  const handleTogglePin = async (event, ticket) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      if (ticket.isPinned) {
        await api.delete(`/tickets/${ticket.id}/pin`);
      } else {
        await api.post(`/tickets/${ticket.id}/pin`);
      }

      await loadTickets(true);
    } catch (err) {
      toastError(err);
    }
  };

  const handleMarkUnreadFromCard = async (
    event,
    ticket
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (!ticket?.id) {
      return;
    }

    try {
      const response = await api.put(
        `/tickets/${ticket.id}/unread`
      );

      const unreadMessages = Math.max(
        Number(
          response?.data?.unreadMessages ??
          ticket?.unreadMessages ??
          0
        ),
        1
      );

      // Atualiza imediatamente o card,
      // sem precisar abrir/recarregar a conversa.
      setTickets(previous =>
        previous.map(item =>
          Number(item.id) === Number(ticket.id)
            ? {
                ...item,
                unreadMessages
              }
            : item
        )
      );

      // Sincroniza a lista com o backend/socket.
      loadTickets(true);
    } catch (err) {
      toastError(err);
    }
  };

  const currentLabel = useMemo(() => {
    if (!activeTab) {
      return "Conversas";
    }

    if (activeTab.type === "mine") {
      return "Conversas sob minha responsabilidade";
    }

    if (activeTab.type === "whatsapp") {
      return `Conexão ${activeTab.label}`;
    }

    if (activeTab.type === "closed") {
      return "Conversas finalizadas";
    }

    return "Todas as conversas";
  }, [activeTab]);

  const currentCount =
    activeFiltersCount > 0 ||
    searchParam.trim() ||
    onlyUnread ||
    (
      activeTab?.type !== "closed" &&
      appliedFilters.withoutAi
    )
      ? listCount
      : counts[active] ?? listCount;

  return (
    <>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        withInitialMessage
        onClose={ticket => {
          setNewTicketModalOpen(false);

          if (ticket?.uuid) {

            // O botao + sempre abre a conversa
            // dentro do escopo "Meus".
            setActive("mine");

            setSearchParam("");

            setOnlyUnread(false);

            if (
              typeof setAppliedFilters ===
              "function"
            ) {
              setAppliedFilters(
                createEmptyFilters()
              );
            }

            setPageNumber(1);

            history.push(
              `/tickets/${ticket.uuid}`
            );
          }
        }}
      />

      <div className={classes.root} data-guide="conversations-center">
      <div className={classes.top}>
        <div className={classes.header}>
          <div className={classes.heading}>
            <div className={classes.titleRow}>
              <Typography component="h1" className={classes.title}>
                Conversas
              </Typography>

              <span className={classes.activeCount}>
                {currentCount}
              </span>
            </div>

            <Typography className={classes.subtitle}>
              {currentLabel} em tempo real
            </Typography>
          </div>

          <IconButton
            className={classes.addButton}
            aria-label="Nova conversa"
            title="Nova conversa"
            onClick={() => setNewTicketModalOpen(true)}
          >
            <AddIcon />
          </IconButton>
        </div>

        <div className={classes.searchRow}>
          <div
            className={classes.search}
            style={{ flex: 1, minWidth: 0 }}
          >
            <SearchIcon className={classes.searchIcon} />

            <InputBase
              className={classes.searchInput}
              placeholder="Buscar contato ou mensagem..."
              value={searchParam}
              onChange={event => setSearchParam(event.target.value)}
            />
          </div>

          {activeTab?.type !== "closed" ? (
            <Tooltip
              title={
                onlyUnread
                  ? "Mostrar todas"
                  : "Mostrar somente não lidas"
              }
              placement="bottom"
            >
              <IconButton
                className={classes.filterButton}
                aria-label="Conversas não lidas"
                onClick={() =>
                  setOnlyUnread(current => !current)
                }
                style={
                  onlyUnread
                    ? {
                        color: "#0057FF",
                        borderColor: "#0057FF",
                        backgroundColor:
                          "rgba(0,87,255,.08)"
                      }
                    : undefined
                }
              >
                <VisibilityOffOutlinedIcon
                  style={{ fontSize: 19 }}
                />
              </IconButton>
            </Tooltip>
          ) : null}

          {activeTab?.type !== "closed" ? (
            <Tooltip title="Aguardando atendimento" placement="bottom">
              <IconButton
                className={classes.filterButton}
                aria-label="Aguardando atendimento"
                aria-pressed={Boolean(appliedFilters.withoutAi)}
                onClick={() =>
                  setAppliedFilters(prev => ({
                    ...prev,
                    withoutAi: !prev.withoutAi
                  }))
                }
                style={
                  appliedFilters.withoutAi
                    ? {
                        color: "#0057FF",
                        borderColor: "#0057FF",
                        backgroundColor: "rgba(0,87,255,.08)"
                      }
                    : undefined
                }
              >
                <HourglassEmptyIcon style={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          ) : null}

          <IconButton
            className={classes.filterButton}
            aria-label="Filtrar conversas"
            title="Filtrar conversas"
            onClick={handleOpenFilters}
          >
            <Badge
              color="primary"
              badgeContent={activeFiltersCount}
              invisible={activeFiltersCount === 0}
            >
              <TuneIcon style={{ fontSize: 19 }} />
            </Badge>
          </IconButton>
        </div>

        <Popover
          open={filterOpen}
          anchorEl={filterAnchorEl}
          onClose={handleCloseFilters}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
          getContentAnchorEl={null}
          PaperProps={{
            className: classes.filterPaper
          }}
        >
          <div className={classes.filterHeader}>
            <Typography
              style={{
                fontSize: 14,
                fontWeight: 750
              }}
            >
              Filtrar conversas
            </Typography>

            <IconButton
              size="small"
              onClick={handleCloseFilters}
              aria-label="Fechar filtros"
            >
              <CloseIcon style={{ fontSize: 18 }} />
            </IconButton>
          </div>

          <div className={classes.filterBody}>
            {renderFilterSection(
              "Responsáveis",
              "userIds",
              visibleUsers,
              option =>
                Number(option.id) === Number(user?.id)
                  ? `Eu · ${option.name}`
                  : option.name
            )}

            {activeTab?.type !== "closed" ? (
              <div>
                <Typography
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    opacity: 0.62,
                    marginBottom: 4,
                    marginTop: 8
                  }}
                >
                  Tipo de atendimento
                </Typography>

                <FormControlLabel
                  style={{
                    display: "block",
                    margin: 0,
                    minHeight: 30
                  }}
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={Boolean(
                        draftFilters.withoutAi
                      )}
                      onChange={event =>
                        setDraftFilters(prev => ({
                          ...prev,
                          withoutAi:
                            event.target.checked
                        }))
                      }
                    />
                  }
                  label={
                    <span style={{ fontSize: 12 }}>
                      Aguardando atendimento
                    </span>
                  }
                />
              </div>
            ) : null}

            {renderFilterSection(
              "Etiquetas",
              "tagIds",
              filterOptions.tags
            )}

            {renderFilterSection(
              "Conexões",
              "whatsappIds",
              filterOptions.whatsapps
            )}
          </div>

          <div className={classes.filterFooter}>
            <Button
              size="small"
              onClick={clearFilters}
            >
              Limpar
            </Button>

            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={applyFilters}
            >
              Aplicar
            </Button>
          </div>
        </Popover>

        <div className={classes.tabs}>
          {conversationTabs.map(tab => {
            const Icon = tab.icon;
            const selected = active === tab.key;

            return (
              <button
                type="button"
                key={tab.key}
                className={`${classes.tab} ${
                  selected ? classes.tabActive : ""
                }`}
                onClick={() => setActive(tab.key)}
              >
                <Icon className={classes.tabIcon} />

                <span>{tab.label}</span>

                <span
                  className={`${classes.tabCount} ${
                    selected ? classes.tabCountActive : ""
                  }`}
                >
                  {counts[tab.key] || 0}
                </span>
              </button>
            );
          })}
        </div>

        {activeTab?.type === "mine" &&
        Number(companyId) === 75 ? (
          <KoraxPendingClaims
            companyId={companyId}
            socket={socket}
            onClaimed={() => loadTickets(true, 1, false)}
          />
        ) : null}



        {filterChips.length > 0 ? (
          <div className={classes.activeFilters}>
            {filterChips.map(chip => (
              <Chip
                key={`${chip.key}-${chip.id}`}
                size="small"
                className={classes.activeFilterChip}
                label={chip.label}
                onDelete={() =>
                  removeAppliedFilter(chip.key, chip.id)
                }
              />
            ))}
          </div>
        ) : null}
      </div>

      <div
        className={classes.list}
        onScroll={handleListScroll}
      >
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress size={24} />
          </div>
        ) : null}

        {!loading && tickets.length === 0 ? (
          <div className={classes.empty}>
            <div className={classes.emptyIcon}>
              <ForumIcon />
            </div>

            <div className={classes.emptyTitle}>
              Nenhuma conversa encontrada
            </div>

            <div className={classes.emptyText}>
              Tente trocar o filtro ou buscar por outro nome.
            </div>
          </div>
        ) : null}

        {!loading &&
          tickets.map(ticket => {
            const name = ticket.contact?.name || "Contato";
            const unread = Number(ticket.unreadMessages || 0);
            const activeCard = selectedUuid === ticket.uuid;
            const ticketSearchMatch =
              getTicketSearchMatch(ticket);

            return (
              <div
                key={ticket.id}
                className={`${classes.card} ${
                  activeCard ? classes.cardActive : ""
                }`}
                onClick={() => openTicket(ticket)}
              >
                <Badge
                  overlap="circular"
                  color="primary"
                  variant={unread > 0 ? "dot" : "standard"}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right"
                  }}
                >
                  <Avatar
                    className={classes.avatar}
                    src={
                      ticket.contact?.urlPicture ||
                      ticket.contact?.profilePicUrl
                    }
                  >
                    {name.charAt(0).toUpperCase()}
                  </Avatar>
                </Badge>

                <div className={classes.content}>
                  <Typography className={classes.name}>
                    {name}
                  </Typography>

                  <Typography className={classes.message}>
                    {ticket.lastMessage || "Sem mensagens recentes"}
                  </Typography>

                  {ticketSearchMatch ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 6,
                        marginTop: 7,
                        padding: "7px 8px",
                        borderRadius: 9,
                        backgroundColor: "#F8FAFC",
                        border: "1px solid #E2E8F0"
                      }}
                    >
                      <SearchIcon
                        style={{
                          fontSize: 14,
                          marginTop: 1,
                          flexShrink: 0,
                          color: "#0057FF"
                        }}
                      />

                      <div
                        style={{
                          minWidth: 0
                        }}
                      >
                        <div
                          style={{
                            marginBottom: 2,
                            fontSize: 9,
                            fontWeight: 800,
                            color: "#64748B",
                            textTransform: "uppercase",
                            letterSpacing: ".03em"
                          }}
                        >
                          Encontrado na conversa
                        </div>

                        <div
                          style={{
                            fontSize: 11,
                            lineHeight: 1.35,
                            color: "#334155",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden"
                          }}
                        >
                          {renderHighlightedSearchText(
                            buildSearchSnippet(
                              ticketSearchMatch.body,
                              searchParam
                            ),
                            searchParam
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div className={classes.meta}>
                    {ticket.queue?.name ? (
                      <Chip
                        size="small"
                        label={ticket.queue.name}
                        title={`Setor: ${ticket.queue.name}`}
                        style={{
                          backgroundColor: "#EEF2FF",
                          color: "#2563EB"
                        }}
                      />
                    ) : null}

                    {(
                      ticket.aiActive === true ||
                      (
                        ticket.aiActive == null &&
                        !ticket.userId &&
                        ticket.whatsapp?.promptId
                      )
                    ) ? (
                      <Chip
                        size="small"
                        label="IA ativa"
                        title={
                          ticket.aiActive === true
                            ? "Atendimento realizado pela IA"
                            : "IA padrão deste WhatsApp ativa"
                        }
                        style={{
                          backgroundColor: "#EDE9FE",
                          color: "#6D28D9"
                        }}
                      />
                    ) : ticket.user?.name || ticket.userId ? (
                      <Chip
                        size="small"
                        label="Humano"
                        title="Atendimento humano"
                        style={{
                          backgroundColor: "#F1F5F9",
                          color: "#475569"
                        }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label="Aguardando atendimento"
                        title="Aguardando atendimento humano"
                        style={{
                          backgroundColor: "#F1F5F9",
                          color: "#475569"
                        }}
                      />
                    )}

                    {ticket.whatsapp?.name ? (
                      <Chip
                        size="small"
                        label={ticket.whatsapp.name}
                        title={`Origem: ${ticket.whatsapp.name}`}
                        style={{
                          backgroundColor: "#DCFCE7",
                          color: "#166534"
                        }}
                      />
                    ) : null}

                    {ticket.user?.name ? (
                      <Chip
                        size="small"
                        label={ticket.user.name}
                        title={`Responsável: ${ticket.user.name}`}
                        style={{
                          backgroundColor: "#F1F5F9",
                          color: "#475569"
                        }}
                      />
                    ) : null}

                    {(() => {
                      const contactTags = Array.isArray(ticket?.contact?.tags)
                        ? ticket.contact.tags
                        : [];

                      const ticketTags = Array.isArray(ticket?.tags)
                        ? ticket.tags
                        : [];

                      const displayTags = [
                        ...contactTags,
                        ...ticketTags.filter(
                          ticketTag =>
                            !contactTags.some(
                              contactTag =>
                                Number(contactTag.id) === Number(ticketTag.id)
                            )
                        )
                      ];

                      return (
                        <>
                          {displayTags.slice(0, 2).map(tag => (
                            <Chip
                              key={`lead-tag-${ticket.id}-${tag.id}`}
                              size="small"
                              label={tag.name}
                              title={`Etiqueta: ${tag.name}`}
                              style={{
                                height: 20,
                                fontSize: 10,
                                fontWeight: 700,
                                backgroundColor: "#F8FAFC",
                                color: "#334155",
                                border: `1px solid ${tag.color || "#CBD5E1"}`
                              }}
                            />
                          ))}

                          {displayTags.length > 2 ? (
                            <Tooltip
                              arrow
                              placement="top"
                              title={
                                <div>
                                  {displayTags.slice(2).map(tag => (
                                    <div
                                      key={`lead-extra-tag-${ticket.id}-${tag.id}`}
                                    >
                                      {tag.name}
                                    </div>
                                  ))}
                                </div>
                              }
                            >
                              <Chip
                                size="small"
                                label={`+${displayTags.length - 2}`}
                                style={{
                                  height: 20,
                                  fontSize: 10,
                                  fontWeight: 800,
                                  backgroundColor: "#F1F5F9",
                                  color: "#64748B"
                                }}
                              />
                            </Tooltip>
                          ) : null}
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className={classes.right}>
                  <div className={classes.rightTop}>
                    {activeTab?.type !== "closed" ? (
<button
                      type="button"
                      className={`${classes.pinButton} ${
                        unread > 0
                          ? classes.pinButtonActive
                          : ""
                      }`}
                      title={
                        unread > 0
                          ? "Conversa não lida"
                          : "Marcar como não lida"
                      }
                      aria-label="Marcar como não lida"
                      onClick={event =>
                        handleMarkUnreadFromCard(
                          event,
                          ticket
                        )
                      }
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M3 3l18 18" />
                        <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                        <path d="M9.9 4.2A10 10 0 0 1 12 4c5 0 9 4 10 8a11 11 0 0 1-2.1 3.9" />
                        <path d="M6.6 6.6C4.2 8 2.7 10 2 12c1 4 5 8 10 8a10 10 0 0 0 3.4-.6" />
                      </svg>
                    </button>
) : null}

                    <button
                      type="button"
                      className={`${classes.pinButton} ${
                        ticket.isPinned ? classes.pinButtonActive : ""
                      }`}
                      title={
                        ticket.isPinned
                          ? "Desafixar conversa"
                          : "Fixar conversa"
                      }
                      aria-label={
                        ticket.isPinned
                          ? "Desafixar conversa"
                          : "Fixar conversa"
                      }
                      onClick={event => handleTogglePin(event, ticket)}
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M8 3h8l-1.4 5.2v2.3l2.8 2.2V15H13v6l-1 2-1-2v-6H6.6v-2.3l2.8-2.2V8.2L8 3z" />
                      </svg>
                    </button>

                    <span className={classes.time}>
                      {formatTime(ticket.updatedAt)}
                    </span>
                  </div>

                  {unread > 0 ? (
                    <span className={classes.unread}>
                      {unread > 99 ? "99+" : unread}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
      </div>
      </div>
    </>
  );
};

export default KoraxConversationsManager;
